// WHO MAY REACH WHICH URL. The whole permission model, and the only statement of
// it — a page, the API behind that page, and everything nested under either.
//
// ⚠️ IT IS SPARSE, AND THAT IS THE DESIGN. A path appears below ONLY where the
// answer differs from its parent's. Everything else inherits, and inheriting is
// silent: /scripts/videos, /scripts/editor, /sourcing/insights, /sourcing/[id],
// /onboarding/first30 and every page beneath it, /settings/users,
// /settings/onboarding-template and the graded-run pages are all absent from
// this file on purpose. They are not omissions and they must not be "fixed" by
// adding them — a line that merely restates its parent is a second place for the
// answer to live, and the day the two disagree there is nothing to say which
// was meant.
//
// So CHANGING WHO SEES WHAT IS A ONE-LINE EDIT. Giving Creator Expert the
// scripts pages is adding a word to the /scripts line. There is no second table,
// no route file to touch, and no API to keep in step.
//
// ─── The rule this replaces ─────────────────────────────────────────────────
//
// lib/nav-config.ts used to be the permission as well as the sidebar, and said
// so at length: "leaving a route off a role's list is the whole gate". That was
// only ever true where something consulted the list, and consulting it was
// OPT-IN PER ROUTE DIRECTORY. /scripts/videos and /scripts/editor consulted
// nothing for the life of the app, and it could not be observed — Scripts was on
// all six roles' navs, so canOpen was true for everybody and a gate would have
// refused nobody. The seventh role (creator_expert, the first without Scripts)
// walked in and read a named creator's week by id.
//
// It also made one list do two jobs: what is in the sidebar and in what order,
// AND what a role may open. That is why isNavActive carries three hand-written
// carve-outs. Those are highlighting concerns and nothing else now; nav-config
// is presentation, this file is the permission.

import type { Persona } from "@/lib/persona-types";

export type Role = Persona["role"];

/**
 * Everybody who works here. Spelled out so a new role must be placed by hand.
 *
 * ⚠️ A CREATIVE SPECIALIST STANDS BESIDE EVERY `am` IN THIS FILE BUT FOUR, and
 * that is the whole of their page permission: they start on an AM's powers.
 * What narrows them is their brands, below the gate — the same `persona.brands`
 * and `reachFor` that narrow an AM to their pod. The four are the Client Inbox,
 * Action Items, the Operations Inbox and the /settings section (for Your
 * Slack), each opened to AMs alone and each saying why on its line. Widen or
 * narrow the AM here and ask the same question of the CS on the same line.
 */
const EVERY_WORKING_ROLE: readonly Role[] = [
  "creator", "writer", "brand", "am", "creative_specialist", "am_ops", "admin",
];

/**
 * No session needed, or none that this table decides. Signing in is not being
 * let in — the root layout still renders <AccessPending> for somebody with a
 * session and no roster row, and the proxy still bounces anyone with no
 * session at all.
 */
export const PUBLIC = "public" as const;

type Access = readonly Role[] | typeof PUBLIC;

/**
 * THE TABLE. Sparse, nested, and ordered by nothing — lookup walks up from the
 * path, so the order here is for a human reading it.
 *
 * ⚠️ A CHILD MAY ONLY EVER BE NARROWER THAN ITS PARENT. That invariant is what
 * makes inheritance safe to reason about: reaching a page means passing every
 * gate above it, so a child that is WIDER is a child nobody can actually reach
 * through the app and a lie about who holds it. It is checked, not trusted —
 * tests/unit/route-access.test.ts fails on a widening edit and names both nodes.
 */
export const ROUTE_ACCESS: Record<string, Access> = {
  "/meetings/fathom": ["am", "admin"],
  // A Slack sign-in link lands at /login/continue with NO SESSION — that is its
  // entire purpose — and /signin is where the proxy sends anyone without one.
  // Gating either is an infinite "sign in to sign in". proxy.ts says the
  // same thing in capitals about its own matcher.
  "/signin": PUBLIC,
  "/login": PUBLIC,

  // 🚨 THE PRINT PAGES: PUBLIC HERE, GATED BY A TOKEN IN THE PAGE. Gotenberg's
  // Chromium opens them over the compose network with no session, so neither
  // this table nor the proxy can ask it anything. Each page's first act is
  // verifyPrintToken for its own scope (lib/server/print-token.ts) — the FIRST
  // wall, and the one thing to check in a diff that adds a print page
  // (tests/unit/print-pages.test.ts). Outside the proxy's matcher for the same
  // reason.
  //
  // The SECOND wall is Caddy: it answers every /print/* 404, because the
  // renderer reaches the app by its compose alias and never through the front
  // door, so no print page is reachable from the internet at all. Neither wall
  // may be the only one — the token is what protects a page from anything
  // already inside the compose network, and Caddy is what protects it from a
  // page that forgets the token.
  "/print": PUBLIC,

  // ─── The work ────────────────────────────────────────────────────────────
  // Everyone but the casting role, whose two pages are elsewhere entirely. The
  // node covers /scripts/videos AND /scripts/editor, which is the pair that was
  // ungated: the editor has no nav entry of its own and never needed one, it is
  // what a video on the Scripts page opens into.
  "/scripts": EVERY_WORKING_ROLE,

  // The two reference pages, narrower: they are written for the person holding
  // the pen. An AM's answer to "why did this score that" is the Rubric.
  "/scripts/guide": ["creator", "writer"],
  "/scripts/faq": ["creator", "writer"],

  // A brand's cadence. THE AM'S, OPS' AND THE ADMIN'S. It was the first two
  // only, on the rule that an admin reaches a work surface by BECOMING the role
  // that owns it — and that rule still holds for the pages where the lens
  // changes what you see. This one it did not help with: the cadence is a brand's
  // and not an AM's, so an admin who wanted to read or fix one had to guess which
  // AM to put on first, and the page they landed on was the same page either way.
  //
  // ⚠️ IT IS NOT ONLY A PAGE. `brand_schedule`, `brand_commitment` and
  // `brand_approval` all answer to this node through STORE_KIND_ROUTE, so an
  // admin may now WRITE a cadence, a commitment and an approval seat as well as
  // read them. That is the intent — the writes are the page — and it is the one
  // thing to re-read before narrowing this line again.
  "/scripts/schedule": ["am", "creative_specialist", "am_ops", "admin"],

  // ─── The agency's own surfaces ───────────────────────────────────────────
  // The three roles that run the agency. Scoping happens in the query, not
  // here: an AM opens the same page an admin does and sees their own pod.
  "/throughput": ["am", "creative_specialist", "am_ops", "admin"],
  // THE BOOK — every brand, side by side, with what each one is actually doing,
  // and the record beneath it. THE SAME THREE AS /throughput since 2026-09-19:
  // it was admin alone for a day, then admin and AM Ops, and an AM now reads it
  // too, because the Weekly Report the AM takes into the brand meeting lives
  // under this node and Brand Health opens with it.
  //
  // ⚠️ THE WIDENING IS SAFE ONLY BECAUSE THE DATA IS SCOPED BELOW THE GATE, and
  // that is what to re-check before widening anything else here. `readBook`
  // draws only the reader's pods, `readRecord` returns null (a 404) for a brand
  // outside them, and `setBrandKnobAction` refuses a write outside them — each
  // asks `reachFor(lens)`, so an AM sees their own pod and nothing else. This
  // line decides who may open the page; the reach decides which brands are on it.
  //
  // ⚠️ NEVER THE `brand` ROLE, and that was the original reason this line
  // exists rather than sitting inside a wider node. A brand persona is one
  // brand's external staff (lib/persona-scope.ts), and this page's entire job is
  // comparing brands to each other — nor, for the same reason, will it ever show
  // one brand the report written about it.
  "/brands": ["am", "creative_specialist", "am_ops", "admin"],
  // ⚠️ AND THE WEEKLY REPORT INHERITS THIS LINE, deliberately and with nothing of
  // its own: /brands/weekly, /brands/<slug>/weekly and every report beneath it
  // are the same three roles, because the report is the reason an AM holds this
  // node at all. They were held narrower — admin only — while the feature was
  // being built, by two lines here and a `WEEKLY_REPORT_RELEASE_HOLD` constant;
  // its release deleted them rather than widening them, because a line
  // restating its parent is the second place for the answer to live that the
  // header warns about. The PDF route rides the same node through apiGate
  // (/api/brands/<slug>/weekly/<week>/pdf), and the pod scoping below the gate is
  // what makes all of it safe (`reportSettingsFor` answers null outside it).
  //
  // ⚠️ AND AFFILIATE CAMPAIGNS INHERIT /brands TOO, with nothing of their own:
  // the work table (/brands/affiliates), each brand's page beneath it
  // (/brands/affiliates/<slug>, `new` under it) and the metrics
  // (/brands/affiliate-metrics) are the same three roles. They were held for the
  // admin by two lines while they were built; their release deleted the lines
  // rather than widening them, as the Weekly Report's did. The pod scoping is
  // below the gate again: `affiliateBrand` answers null (a 404) for a brand
  // outside the reader's reach, and every affiliate action and API route asks it
  // after the gate.
  //
  // ⚠️ CLIENT SLACK'S PAGES: a narrowing of /brands by a line per page, never
  // the brand role. Response Times is AM Ops' and the admin's. The Client Inbox
  // (each wait's popup, /brands/inbox/<wait>, and its marks under it) and
  // Action Items (each item's popup and New action item under it) are the AMs'
  // too, since the Operations Inbox opened to them: an AM answers their own
  // clients. The pages scope their brands by `reachFor` below the gate, so an
  // AM reads their own pod and nothing else.
  //
  // ⚠️ NOT THE CREATIVE SPECIALIST, who stands beside the AM almost everywhere
  // else in this file: these two were opened to AMs alone. That is why the
  // lines name three roles rather than being deleted into /brands' four, and
  // widening them to the CS is a decision of its own.
  "/brands/response-times": ["am_ops", "admin"],
  "/brands/inbox": ["am", "am_ops", "admin"],
  "/brands/action-items": ["am", "am_ops", "admin"],
  // THE OPERATIONS INBOX: every task a seat owes, a card each. AM Ops start
  // their day here (their pod's Ops seat) and an AM finds what was escalated to
  // their lead seat; the page narrows what each person sees to their own seats,
  // and only an admin may widen it to Active or All. Not the Creative
  // Specialist: they sit in no seat, so their inbox would always be empty.
  "/inbox": ["am", "am_ops", "admin"],
  // Covers the checklist, its insights fold and every client's own copy.
  "/onboarding": ["am", "creative_specialist", "am_ops", "admin"],
  // The same three, plus the role for whom this IS the app. Covers Creator
  // Selection, Selection Insights and the casting a person drills into.
  "/sourcing": ["am", "creative_specialist", "am_ops", "admin", "creator_expert"],

  // ─── Reports ─────────────────────────────────────────────────────────────
  // HOURS OF WORK DELIVERED, a week at a time. AM OPS AND THE ADMIN, which
  // is /brands' list rather than /throughput's: hours worked is a console-side
  // figure about the agency's own staffing, and an AM reading how many hours
  // Ops billed for their pod's clients is a different conversation from an AM
  // reading their pod's throughput.
  //
  // ⚠️ THE API INHERITS IT AND MUST NEVER BE LISTED. apiGate strips /api off
  // POST /api/client-hours/hours and asks canOpen about /client-hours/hours,
  // which walks up to this node — so the page and the write cannot disagree
  // about who may enter a week's hours (tests/unit/api-url-mirrors-page.test.ts).
  "/client-hours": ["am_ops", "admin"],

  // ─── The console ─────────────────────────────────────────────────────────
  "/feedback": ["am_ops", "admin"],
  // EVERY SKILL PAGE, /skills/<slug>: the app's own instructions — each prompt word for word, its versions,
  // its calls and what they cost, and its daily limit. AM Ops' and the
  // admin's. One node where there were seven, a narrowing under each area that
  // runs a skill (/brands/weekly/skills, /brands/affiliates/skills,
  // /brands/client-slack/skills, /sourcing/skills, /onboarding/skills,
  // /meetings/skills, /scripts/rubric). The old paths redirect here
  // (next.config.mjs). The jobs and presses read the prompt FILES, never these
  // pages, so nobody's work changes with who may read them. The comment kinds
  // below (`rubric_comment`, `skill_comment`, `affiliate_skill_comment`,
  // `client_slack_skill_comment`) name this subtree, so each rail narrows with it.
  "/skills": ["am_ops", "admin"],
  // Graded Runs and the run pages beneath it.
  "/admin": ["am_ops", "admin"],
  // HOW THE APP ITSELF IS DOING: the Jobs page with each job's and run's view
  // (a run's log lines are on it), Resources (CPU and memory of the box, every
  // container and the processes inside them) and App health (request times,
  // failures, statements). The admin's, one node for the three, so the API
  // that mirrors them sits under one prefix too. Their old addresses under
  // /admin and /settings/integrations redirect here (next.config.mjs).
  "/monitoring": ["admin"],

  // ⚠️ A SECTION, NOT A PAGE, AND THAT IS WHAT FIXED IT. /settings used to be
  // the Drive grant — admin only — with /settings/users and
  // /settings/onboarding-template beneath it held by admin AND AM Ops. A child
  // wider than its parent, which is precisely why page-gate.tsx had to document
  // that these two "cannot use a layout" and carry a hand-written canOpen call
  // instead. The admin-only page moved down to /settings/integrations and the
  // section became the union, so the nesting holds and the special case is gone:
  // every gated server page now asks through the same gatePage(ownPath) the
  // layouts run, and none of them names a role.
  //
  // ⚠️ AND THE AM HOLDS THE SECTION TOO, FOR ONE PAGE: Your Slack
  // (/settings/slack), their own Slack connection for replying from the Client
  // Inbox and reminding a creator from the Operations Inbox. So every other page
  // under it that AM Ops holds has a line of its own below, the admin's and AM
  // Ops', and a NEW PAGE UNDER /settings IS AN AM'S UNTIL IT IS GIVEN ONE.
  "/settings": ["admin", "am_ops", "am"],
  // The roster, brands and their grants, every model call's cost, and the
  // onboarding template: AM Ops' and the admin's, never an AM's.
  "/settings/users": ["admin", "am_ops"],
  "/settings/brands": ["admin", "am_ops"],
  "/settings/llm-costs": ["admin", "am_ops"],
  "/settings/onboarding-template": ["admin", "am_ops"],
  // The Drive grant and the four sourcing feed syncs — one Workspace account
  // authorizing the server, and the importers that spend on it. The admin's.
  // Reacher writes beneath it (/settings/integrations/reacher-writes: the pool of
  // Reacher keys the affiliate writes spend, and every write) inherits it: the
  // admin's, while the affiliate pages are the AM's too.
  "/settings/integrations": ["admin"],
  // Every model call's full input and output: GET /api/settings/llm-costs/audit,
  // the words behind each receipt on the costs page. The admin's alone: AM Ops
  // reads the costs, never the words.
  "/settings/llm-costs/audit": ["admin"],
  // The feature flags: switches that were the host's .env, set here with no
  // restart. A page of its own, the admin's.
  "/settings/flags": ["admin"],
  // THE OPS KEYS: mint and revoke the read-only keys machines read the
  // monitoring pages' API with. The admin's, as /monitoring is.
  "/settings/ops-keys": ["admin"],
  // THE OPERATIONS INBOX'S SETTINGS: every task type, the Active grid, who
  // sits in each seat and where each workstream escalates. The admin's, a line
  // of its own because /settings is AM Ops' too; its write
  // (/api/settings/operations-inbox) inherits it.
  "/settings/operations-inbox": ["admin"],
  // YOUR SETTINGS: the scope your Operations Inbox opens on. The admin's, as
  // only an admin has a scope to choose (everyone else always sees My).
  "/settings/you": ["admin"],
};

/** The listed ancestors of a path, deepest first. "/" is never one of them. */
function ancestry(path: string): string[] {
  const parts = path.split("/").filter(Boolean);
  const out: string[] = [];
  for (let i = parts.length; i > 0; i--) out.push("/" + parts.slice(0, i).join("/"));
  return out;
}

/**
 * The node that decides this path — the deepest listed ancestor — or null when
 * nothing in the table owns it.
 *
 * ⚠️ null IS A REFUSAL, NOT A GAP, and the difference matters at the root. If
 * the walk fell back to some catch-all "/" entry, every unlisted path in the
 * app would inherit it, and a route added tomorrow under a name nobody listed
 * would be public rather than unreachable. Failing closed is what lets this
 * table be sparse at all.
 */
export function nodeFor(path: string): string | null {
  for (const node of ancestry(path)) {
    if (node in ROUTE_ACCESS) return node;
    const pattern = PATTERN_NODES.find((p) => matchesPattern(p, node));
    if (pattern) return pattern;
  }
  return null;
}

/**
 * The nodes with a "[segment]" in them — a place in the URL that is a value, a
 * brand's slug, rather than a name.
 *
 * ⚠️ NO NODE CARRIES ONE TODAY, and the machinery stays for the reason it was
 * written. Every node used to be a literal, because every narrowing sat on a
 * fixed path; the Weekly Report's per-brand pages sit UNDER a slug
 * (/brands/<slug>/weekly), and while they were held narrower than /brands for
 * their build (a hold since lifted) a literal could not name "every brand's
 * weekly".
 * The alternative was a role check inside each page, a second place for the
 * answer to live. The next narrowing under a slug will want this, and the guard
 * below is what keeps it honest.
 *
 * ⚠️ WHO WOULD REACH A PATTERN. Not the gates: gatePage and gateFor pass the
 * ROUTE STRING ("/brands/[slug]/weekly/[weekStart]"), whose ancestors are looked
 * up literally. A pattern serves CONCRETE URLs: the persona switcher's canOpen on
 * the pathname (components/persona-topbar.tsx), a link asking canOpen before it
 * offers itself, an apiGate route. (isNavActive does not ask this table; it has
 * its own weekly carve-out in lib/nav-config.ts.)
 *
 * 🚨 A PATTERN MAY ONLY NARROW A LISTED SUBTREE. It is consulted only when the
 * literal part in front of its first "[…]" is itself a listed node, so every
 * path it can match already had an answer from that node, and the pattern can
 * only replace it. A pattern with no listed prefix ("/[section]/report") would
 * match paths that resolve to null — a refusal — and WIDEN them; it is dropped
 * here, and tests/unit/route-access.test.ts fails on it so it is not dropped
 * silently.
 */
export function patternPrefix(node: string): string {
  const cut = node.indexOf("/[");
  return cut === -1 ? node : node.slice(0, cut);
}

const PATTERN_NODES: string[] = Object.keys(ROUTE_ACCESS).filter(
  (n) => n.includes("[") && patternPrefix(n) !== "" && patternPrefix(n) in ROUTE_ACCESS,
);

/** "/brands/[slug]/weekly" matches "/brands/organic-india/weekly": same depth, "[…]" is any one segment. */
function matchesPattern(pattern: string, path: string): boolean {
  const want = pattern.split("/");
  const got = path.split("/");
  if (want.length !== got.length) return false;
  return want.every((seg, i) => (seg.startsWith("[") && seg.endsWith("]") ? got[i] !== "" : seg === got[i]));
}

/**
 * MAY THIS ROLE OPEN THIS PATH — and, because they are the same question, may
 * they run what is on it. The layout gate, each server page's own gatePage call
 * (lib/server/page-gate.tsx) and the API gate all end up here, and none of them
 * holds a role list of its own.
 *
 * ⚠️ ROLE, NOT IDENTITY. Callers pass the ACTIVE LENS's role. An admin wearing
 * a creator's lens gets the creator's answers; their identity buys them nothing
 * here, and the only thing it buys them anywhere is the dropdown that put the
 * lens on.
 *
 * "/" is the one path with no node and no refusal: it is a redirect stub that
 * renders nothing and sends every persona to their own home (app/page.tsx).
 */
export function canOpen(role: Role | null | undefined, path: string): boolean {
  if (path === "/") return true;

  const node = nodeFor(path);
  if (node === null) return false;

  const access = ROUTE_ACCESS[node];
  if (access === PUBLIC) return true;
  // A persona with no access at all is refused everything that is not PUBLIC.
  // They never see the refusal: the root layout renders <AccessPending> in place
  // of the page.
  return role ? access.includes(role) : false;
}

/**
 * The API path for a page path, and the whole of the mapping: /api/<the page>.
 *
 * ⚠️ THE MIRROR IS THE MECHANISM, AND IT IS FINISHED. apiGate takes no page:
 * it strips this prefix off its own request URL and asks canOpen about what is
 * left (lib/server/api-gate.ts). A route cannot name a page other than the one
 * it lives under, because it is never asked — so a page and its API cannot
 * drift into disagreeing about who is allowed in, and moving a route between
 * directories is how you change who may call it.
 *
 * Every route.ts under app/api either rides that gate or is named in one of the
 * two exemption lists below. tests/unit/api-url-mirrors-page.test.ts fails on one
 * that is on neither, which is what stops the next route being added ungated —
 * the failure /scripts/videos and /scripts/editor had for the life of the app.
 *
 * ⚠️ THE PATH IT DERIVES IS THE CONCRETE ONE, dynamic segments filled in:
 * /api/onboarding/first30/clients/nomadix → /onboarding/first30/clients/nomadix.
 * nodeFor walks up from it, so the answer is /onboarding's either way, and a
 * [slug] is not a hole in the walk.
 */
export const API_PREFIX = "/api";

/** "/api/sourcing/run" → "/sourcing/run". Null for anything not under /api. */
export function pagePathForApi(apiPath: string): string | null {
  if (apiPath === API_PREFIX) return "/";
  if (!apiPath.startsWith(`${API_PREFIX}/`)) return null;
  return apiPath.slice(API_PREFIX.length);
}

/**
 * THE ROUTES THAT DO NOT RIDE THE PAGE GATE — every one of them, by name, with
 * the reason it is there. There are TWO lists below because there are two
 * different exemptions, and only one of them is dangerous.
 *
 * ⚠️ THESE ARE EXEMPTION LISTS, NOT A SECOND PERMISSION TABLE. Being on one buys
 * a route nothing: it says only "this one is not gated by the page tree, and
 * somebody checked why". The check itself still lives in the route file. What
 * the lists buy the APP is that a route on neither of them and not on apiGate
 * fails tests/unit/api-url-mirrors-page.test.ts — so the way to add an ungated
 * route is to write a sentence here saying you meant to, and the way to add one
 * by accident does not exist.
 *
 * ⚠️ AND THE SPLIT IS THE POINT. One list used to carry both meanings under the
 * name IDENTITY_ONLY, which worked only while every entry but one derived to a
 * path no node owned — "identity" was already the family name rather than a
 * claim about every row (/api/build-id checks nothing at all, on purpose). The
 * moment the OAuth grants took their honest URLs under /settings/integrations,
 * four more entries landed INSIDE a gated subtree, and a single list would have
 * said the same word about "nothing owns this URL" and "a page owns this URL and
 * we are deliberately not asking it". Those are not the same claim and they do
 * not carry the same risk: the first is a fact about the table that the test can
 * check on its own, the second is a decision a human has to agree with. So each
 * list asserts the property its name states, and the second one is short, named
 * route by route in the test, and meant to be read in full.
 */

/**
 * NO PAGE OWNS THE URL, so there is nothing for apiGate to derive from. Strip
 * /api off any of these and nodeFor answers null — which is a refusal, so
 * putting one of them on the gate would answer 403 to the entire company, admin
 * included. The route says here what it asks INSTEAD; most ask identify(),
 * because the question really is "who is this" and a lens cookie must not answer
 * it, and the rest answer to something that is not a session at all: Slack's own
 * HMAC signature, a build-time constant, or (for /api/build-id and /api/health)
 * nothing.
 */
export const NO_PAGE_OWNS_IT: Record<string, string> = {
  "/api/fathom/callback": "OAuth return: signed-in owner plus matching short-lived state cookie",
  "/api/fathom/webhooks/[connectionId]": "Fathom HMAC signature, bounded raw body, and active connection owner",
  // NextAuth's own handlers. Gating sign-in on a session is "sign in to sign in".
  "/api/auth/[...nextauth]": "the sign-in mechanism itself; NextAuth checks its own state and CSRF",
  // Connect Slack's return leg: under sign-in's callback so Slack takes it as a
  // subdirectory of a registered Redirect URL.
  "/api/auth/callback/slack/connect":
    "a browser mid-redirect from Slack; the state cookie, then the IDENTITY's role (AM Ops and admins), as Connect Slack asked it",
  // One opaque string Next already puts in every asset URL, and the page that
  // most needs to ask is /signin, which has no session by definition.
  "/api/build-id": "deliberately unauthenticated — a stale /signin must be able to ask",
  // What the deploy polls, from inside the container, with no session. It reads
  // both databases and answers only ok or not, plus the build id above.
  "/api/health": "deliberately unauthenticated — the deploy asks it with no session, and it says only ok or not",
  // Half the crashes worth hearing about happen where there is no session yet.
  // Open on purpose, with a rate limit, a body cap and per-field truncation.
  "/api/client-error": "deliberately open — a crash before sign-in is the report worth having",
  // Every signed-in page posts its own opens here, so no one page owns it.
  "/api/page-open": "identify(): any signed-in, provisioned person's own page opens, counted under the session's role",
  "/api/slack/events": "Slack, not a person — verified by HMAC against the signing secret",
  "/api/grade": "actingLens() — grading is any signed-in seat's, on a script in its scope, and the row is written here",
  "/api/grade/reset": "admin on the IDENTITY: 'the selected role is chrome, not privilege'",
  // Authorized by OWNERSHIP of the draft, which no page node can express.
  "/api/notify/submit": "the lens must own the script it is announcing; a page cannot say that",
  // ⚠️ THE ONE ENTRY THAT IS EXEMPT FROM THE URL AND GATED ANYWAY. Its URL cannot
  // answer — /api/store strips to /store, which no node owns — so the route reads
  // STORE_KIND_ROUTE at the bottom of this file and asks canOpen about the page
  // the written kind belongs to. GET stays open by decision: the gates go on
  // POST changes and on displaying the UI, not on GET.
  "/api/store": "one endpoint for every kind, so the BODY says which page a write belongs to; GET is open on purpose",
  // 404s unless the build itself was made with the dev bypass compiled in.
  "/api/test/mint-login-token": "a build-time constant, which is stronger than any session check",
  // The same build-time constant, and the Reacher stub: it runs the approvals
  // pass in the app's process for a walkthrough, and cannot reach Reacher.
  "/api/test/affiliate-approvals": "a build-time constant plus REACHER_STUB, which is stronger than any session check",
  // Its twin for the reply queue: the same guards, the same reason.
  "/api/test/affiliate-replies": "a build-time constant plus REACHER_STUB, which is stronger than any session check",
  // And for the drafting pass: the same guards, plus the drafts' own stub, so it
  // can reach neither Reacher nor a model.
  "/api/test/affiliate-drafts": "a build-time constant plus REACHER_STUB and AFFILIATE_DM_REPLIES_STUB, which is stronger than any session check",
  // And for the hourly reads: the same guards; its reads are the stub's, so it
  // can reach neither Reacher nor anything but the e2e stack's own mirror.
  "/api/test/affiliate-reads": "a build-time constant plus REACHER_STUB, which is stronger than any session check",
  // And for the New campaigns' runner: the same guards; its reads are the
  // fixture's and its writes the stub's.
  "/api/test/affiliate-campaigns": "a build-time constant plus REACHER_STUB, which is stronger than any session check",
  // And for the Reacher portal session: the same guards; its refresh is the
  // stub's, so it can never reach the portal.
  "/api/test/reacher-portal": "a build-time constant plus REACHER_STUB, which is stronger than any session check",
  // And for the TikTok pull: the same guards; its browser is the stub's, handed
  // in, so it can never reach TikTok.
  "/api/test/tiktok-pull": "a build-time constant plus REACHER_STUB, which is stronger than any session check",
  // Makes the stub's Creators-step counts slow or busy, so the New campaign's
  // give-up and "busy" paths are walked without a real key.
  "/api/test/reacher-counts": "a build-time constant plus REACHER_STUB, which is stronger than any session check",
  // Holds the store's write lock from a second connection in the app's own
  // process, as a sidecar would: the "busy" answer, walked.
  "/api/test/hold-write-lock": "a build-time constant plus the dev bypass at run time, which is stronger than any session check",
  // Makes the server report another build, as a deploy would: a stale tab's
  // full-load navigation, walked without restarting the store's epoch.
  "/api/test/build-id": "a build-time constant plus the dev bypass at run time, which is stronger than any session check",
  // Makes every job's work wait before it starts: the e2e stubs answer at once,
  // so a job running, reloaded or joined is walked this way.
  "/api/test/long-jobs": "a build-time constant plus the dev bypass at run time, which is stronger than any session check",
  // Answers what the statement guard counted for its own request: the canary
  // that goes red when Next moves its request store.
  "/api/test/statement-budget": "a build-time constant plus the dev bypass at run time, which is stronger than any session check",
  // Answers how often a route read a whole-table reader before and after a
  // write: oncePerRender's request memo, proved on the real server.
  "/api/test/once-per-request": "a build-time constant plus the dev bypass at run time, which is stronger than any session check",
  // Counts access-log lines into the ops file as the ops sidecar would: App
  // health's Requests tab, walked on a stack with no Caddy.
  "/api/test/ops-requests": "a build-time constant plus the dev bypass at run time, which is stronger than any session check",
  // Adds failure counts at past hours into the ops file: an opened Failures
  // row's hourly timeline, walked with a history.
  "/api/test/ops-counts": "a build-time constant plus the dev bypass at run time, which is stronger than any session check",
  // Opens action items as the model does, with a suggestion nobody has
  // confirmed: the Operations Inbox's review card, walked in e2e.
  "/api/test/action-items": "a build-time constant plus the dev bypass at run time, which is stronger than any session check",
};

/**
 * A PAGE DOES OWN THE URL, AND THE ROUTE DELIBERATELY DOES NOT ASK IT. Every
 * entry here derives to a real node — so every entry is a hole in a gated
 * subtree, knowingly punched, and the sentence beside it is the argument for
 * punching it rather than a note that somebody did.
 *
 * ⚠️ THIS IS THE LIST TO BE SUSPICIOUS OF, and it is deliberately hard to grow.
 * The failure it exists to make visible is somebody exempting, say,
 * /api/sourcing/export — a route inside a gated tree, excused into being open to
 * every signed-in seat, with a plausible-sounding sentence beside it. So
 * tests/unit/api-url-mirrors-page.test.ts pins the membership BY NAME: adding a
 * route here means editing that test too, in a diff a reviewer reads.
 *
 * ⚠️ AND ONE SENTENCE COVERS ALL FOUR: THE PAGE GATE ASKS THE WRONG PERSON A
 * QUESTION THEY CANNOT ANSWER. Two shapes of that, and both are here. The first
 * is the lens: apiGate asks canOpen about the ACTIVE LENS, which is right for
 * every work surface and wrong for a grant — an admin wearing a creator's lens
 * is still the person who should be able to connect Drive, and /api/grade/reset
 * writes that rule down in the words these routes reuse: "the selected role is
 * chrome, not privilege."
 * The second is the caller: a browser mid-redirect from Google or Slack, and a
 * Slack workspace admin who need not have a seat in this app at all. There is no
 * session for a page to be asked about, and asking would refuse exactly the
 * person the route exists for.
 */
export const THE_PAGE_IS_NOT_THE_QUESTION: Record<string, string> = {
  // ─── The Drive grant. Its page is /settings/integrations, which is admin. ──
  // So the ROLE the gate would ask for is already right, and that is not why
  // these are here: the gate would ask it of the LENS.
  "/api/settings/integrations/drive/connect":
    "starting the grant is the IDENTITY's, not a lens's — an admin browsing as a creator is still the admin who may connect Drive",
  "/api/settings/integrations/drive/callback":
    "a browser mid-redirect from Google, landing on the same identity check the connect step made",
  // ⚠️ THE FOOTAGE CHECK IS NOT HERE, and it never belonged with the two above.
  // /api/scripts/editor/drive-check rides apiGate: it is the script editor's
  // Upload Footage box asking whether a pasted link is a readable video, so it
  // answers to /scripts like the page that mounts it. Moving it under the
  // grant's URL would have derived /settings/integrations and refused every
  // creator and writer in the app.
  // ─── The Slack install. Same page, and a caller from outside the app. ─────
  "/api/settings/integrations/slack/install":
    "the installer is a Slack workspace admin who need not be a user of this app at all — any page gate would refuse the only person who can run it",
  "/api/settings/integrations/slack/callback":
    "a browser mid-redirect from Slack with no session of ours behind it; the state cookie and our client secret are the check",
};

/**
 * ⚠️ THE FOUR OAUTH ROUTES ABOVE OWN A NODE NOW, AND DID NOT ALWAYS. Their
 * URLs used to be /api/drive/* and /api/slack/oauth/*, which owned no node, so
 * the old single list could describe every row as "no page answers for this".
 * They now sit under the page they belong to and the sentence has to be argued
 * instead: the grant IS the /settings/integrations page's business, and the
 * reason it is not asked is the lens and the caller, not the URL.
 */

/**
 * WHICH PAGE A STORE WRITE BELONGS TO, read off the `kind` in its body.
 *
 * ⚠️ ONE ENDPOINT, FIFTEEN KINDS, AND A URL THAT CANNOT SAY WHICH. Everything
 * the app writes goes through POST /api/store as {kind, id, data} — a draft, a
 * stage move, a comment, a week's post date, a star rating — so the derivation
 * the rest of this file rests on has nothing to work with. /api/store strips to
 * /store, which no node owns, and apiGate would answer 403 to the whole company.
 * The BODY is the only thing that knows which page a write came from, and this
 * is the table that reads it. It is a SECOND question, not a replacement for the
 * first: /api/store stays in NO_PAGE_OWNS_IT because its URL still answers to no
 * page, and this is what the route asks instead.
 *
 * ⚠️ THE VALUE IS THE PAGE THE WRITE IS MADE FROM, NOT THE PAGE IT IS READ ON,
 * and the two differ often enough that the obvious guess gets it backwards. A
 * grade correction is filed from the script editor by whoever disagrees with a
 * grade and is READ on the grade-history rail under /admin; mapping `run_comment`
 * to /admin would take the only channel a non-admin has for disagreeing with a
 * grade away from every creator in the app — components/grade-model-feedback.tsx
 * says exactly that, in capitals, about retiring the widget.
 *
 * ⚠️ SO WHERE TWO PAGES WRITE ONE KIND, THE ANSWER IS THE WIDER OF THEM. The
 * narrower page's holders pass a wider value anyway (a child is never wider than
 * its parent, and /feedback's am_ops+admin are inside /scripts' six roles), while
 * the narrower value refuses writers who are doing nothing wrong. One kind, one
 * page, chosen so that every legitimate writer is admitted.
 *
 * ⚠️ AN UNMAPPED KIND IS REFUSED, and that is the mechanism rather than a rough
 * edge. A kind invented tomorrow with no line here cannot arrive ungated: it is
 * refused on its first write, and tests/unit/store-kind-route.test.ts fails
 * earlier still, at the persist() call site that introduced it. Writing the line
 * is how you say who may write the kind, and there is no way to skip it.
 */
export const STORE_KIND_ROUTE: Record<string, string> = {
  // ─── The script surfaces ─────────────────────────────────────────────────
  // /scripts is the node over both the videos board and the editor, which is
  // where all of these are written. Six working roles; the seventh
  // (creator_expert) holds Creator Selection and nothing else, and this is the
  // line that stops the editor's Post comment box answering them — the hole #196
  // closed in the nav and left open on the wire.
  draft: "/scripts",
  grade: "/scripts",
  stage_override: "/scripts",
  stage_undo: "/scripts",
  video_stage: "/scripts",
  drive_video: "/scripts",
  comment: "/scripts",
  approved_on: "/scripts",
  // Keyed creator+brand+week and written from the videos board, not from the
  // schedule page — the cadence sets the default, these two are what a week
  // actually did. They are also the only two kinds DELETE accepts.
  week_date: "/scripts",
  week_plan: "/scripts",
  // Both halves of "this grade is wrong", filed from the editor's grade widget
  // by whoever holds the script. The consoles that READ them are narrower
  // (/feedback, /admin) and their holders are inside these six.
  run_comment: "/scripts",
  grader_feedback: "/scripts",

  // ─── Narrower than the section they sit in ───────────────────────────────
  // A brand's cadence, written only by the schedule page — the AM's, Ops' and,
  // since the page joined the console's nav, the admin's. Still narrower than
  // /scripts: a creator, a writer and a brand user hold the board and the editor
  // and never the cadence that dates them.
  brand_schedule: "/scripts/schedule",
  // How many videos a week a creator owes a brand — set under the calendar on
  // the same page, by the same holders. It decides how many slots a new week is
  // minted with and what Throughput counts as committed, so it is the AM's
  // number in exactly the way the cadence above it is.
  brand_commitment: "/scripts/schedule",
  // Who gives this brand's FINAL approval — the brand, or their AM. Same page,
  // same holders: it is a property of how a brand's work flows, negotiated and
  // recorded by the AM who negotiates it, exactly as the cadence above it is.
  // It is deliberately not ON /settings/brands, which is identity — the label,
  // the pod, the vendor grants. The admin holds both
  // pages now; what stays split is which page the seat is set on.
  brand_approval: "/scripts/schedule",
  // ─── The skill pages' correction rails ───────────────────────────────────
  // Each kind names the page (or pages) it is written on, all under /skills
  // since the pages moved there, so each follows that
  // node's gate: AM Ops' and the admin's. The KIND NAMES are older than the move
  // and stay as they were, because every stored row and audit row carries one.
  //
  // The SME correction rail down the side of the rubric (Script Grading's
  // page). Written only there.
  rubric_comment: "/skills/script-grading",
  // The Weekly Report's two skill pages, Report Writing and Report Review: two
  // pages, one kind, so it names their common node.
  skill_comment: "/skills",
  // The DM Replies skill page. Its own kind, so the notes follow that page's
  // gate.
  affiliate_skill_comment: "/skills/dm-replies",
  // Client Slack's nine skill pages: nine pages, one kind, so it names their
  // common node.
  client_slack_skill_comment: "/skills",

  // ─── The one that belongs to no page ─────────────────────────────────────
  // ⚠️ "/" IS A DECISION HERE, NOT THE WIDEST HONEST GUESS: anyone logged in may
  // give feedback. So this line is the intent and not a fallback for a kind
  // nobody could place — a narrower value would be a bug against it, and the day
  // somebody wants ratings restricted, this is the line to argue with.
  //
  // It also happens to be the only value that describes where the write comes
  // from. THE STAR WIDGET IS IN THE ROOT LAYOUT, so it is on every page of the
  // app for every role. /feedback is where these are READ (am_ops, admin) and
  // mapping it there would refuse the rating of everybody whose opinion the
  // widget exists to collect — including the Creator Expert, whose pages are
  // nowhere near it. canOpen answers true for "/" by construction: it is the stub
  // that sends every persona to their own home, the one path with no node and no
  // refusal, and being signed in at all is what it takes to get that far.
  feedback: "/",
};
